import { useEffect, useRef, useState } from 'react'

import {
  LIVEKIT_BACKGROUND_EFFECT_CHANGED_EVENT,
  NO_BACKGROUND_EFFECT,
  type BackgroundEffect,
} from '../services/liveKitVideoBackground'
import { readCachedBackgroundEffect } from '../services/backgroundEffectPreference'

/**
 * The background effect the user has chosen (SC-158), outside the pickers that
 * own a choice of their own.
 *
 * Seeded from the cache, so the remembered effect shows on first paint rather
 * than "no effect" until the call reports in. Then the call is the source of
 * truth: when a new pick fails while another effect is running, it reports that
 * the running one is still the choice.
 *
 * The setter is for an optimistic update by a surface that has just asked for
 * a change; the call's report follows and wins.
 *
 * `enabled: false` is for callers that only sometimes need it, such as camera
 * tiles, where only our own tile does: it then reads no storage, adds no
 * listener and reports "no effect", so an effect change does not re-render
 * every remote tile. Switched on later, it reads the cache again, since changes
 * made meanwhile were not heard.
 */
export function useSelectedBackgroundEffect({ enabled = true }: { enabled?: boolean } = {}): [
  BackgroundEffect,
  (effect: BackgroundEffect) => void,
] {
  const [effect, setEffect] = useState<BackgroundEffect>(() =>
    enabled ? readCachedBackgroundEffect() : NO_BACKGROUND_EFFECT
  )
  // The mount's read above already covers the first switch-on.
  const seededRef = useRef(enabled)

  useEffect(() => {
    if (!enabled) return undefined
    if (seededRef.current) seededRef.current = false
    else setEffect(readCachedBackgroundEffect())

    const handleChanged = (event: Event) => {
      const next = (event as CustomEvent<{ effect?: BackgroundEffect }>).detail?.effect
      if (next) setEffect(next)
    }
    window.addEventListener(LIVEKIT_BACKGROUND_EFFECT_CHANGED_EVENT, handleChanged)
    return () => window.removeEventListener(LIVEKIT_BACKGROUND_EFFECT_CHANGED_EVENT, handleChanged)
  }, [enabled])

  return [enabled ? effect : NO_BACKGROUND_EFFECT, setEffect]
}

export default useSelectedBackgroundEffect
